import { withoutHiddenGuesses } from "@/utils/hidden-sam2-guesses";
import { client } from "@augmodo/lebron-browser-client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useGate } from "statsig-react";

/**
 * One of a task's parcels, from GET /v1/jobs/{job_id}/tasks/{task_id}/parcels:
 * the PRIMARY parcel the task was created for, or a CONTEXT parcel (another
 * view of the same shelf captured within a couple of minutes of it).
 * Hand-typed until the generated client picks up the endpoint.
 */
export interface TaskParcel {
  parcel_id: string;
  role: "PRIMARY" | "CONTEXT";
  image_id: string;
  video_id: string | null;
  captured_at: string;
  /** Capture time relative to the primary parcel. */
  offset_ms: number | null;
  image: string;
  optimized_image: string | null;
  /**
   * Empty areas known for this parcel: a labeler's (HUMAN) once it has been
   * labeled, else SAM2's guesses carried over from other tasks' labels.
   */
  annotations: PropagatedAnnotation[];
  /**
   * Only when read with `includeSam2` (the admin view): SAM 2's latest guess
   * for each area on this parcel, including ones a labeler's label has since
   * replaced in `annotations`, and ones the labeler UI hides.
   */
  sam2_annotations?: PropagatedAnnotation[];
  /** The other pending task whose primary this parcel is, if any. */
  task_id: string | null;
  /** SAM2 is still propagating another task's labels onto this parcel. */
  labels_pending: boolean;
}

/** An empty area known for a parcel — see `TaskParcel.annotations`. */
export interface PropagatedAnnotation {
  source: "HUMAN" | "SAM2";
  area_id: string;
  source_task_id: string;
  /** 4 corners, normalized 0-1 to this parcel's frame. */
  points: number[][];
  product_ids: string[];
  score: number;
  /** SAM2 only: its quality check asks for a second look at this guess. */
  needs_review: boolean;
  /** SAM2 only: which check fired, e.g. "area_jump:2.0x"; "" when none. */
  review_reason: string;
}

export type FrameLoadStatus = "loading" | "loaded" | "error";

export interface RelatedFrame extends TaskParcel {
  url: string;
  status: FrameLoadStatus;
}

/** Whether tasks show their related parcels. */
export const useRelatedFramesEnabled = () =>
  useGate("fe_coverage_beta_related_parcels").value;

// Parallel preload slots — enough to keep the pipe full without the far-away
// frames starving the ones next to the task frame.
const PRELOAD_CONCURRENCY = 4;

export const taskParcelsQueryOptions = (
  jobId: string,
  taskId: string,
  { includeSam2 = false }: { includeSam2?: boolean } = {},
) => ({
  // The admin read is its own entry (it carries `sam2_annotations`); the
  // shared prefix still invalidates both.
  queryKey: ["task-parcels", jobId, taskId, ...(includeSam2 ? ["sam2"] : [])],
  queryFn: async (): Promise<TaskParcel[]> => {
    const response = await client.get({
      url: "/v1/jobs/{job_id}/tasks/{task_id}/parcels",
      path: { job_id: jobId, task_id: taskId },
      query: includeSam2 ? { include_sam2: true } : undefined,
    });
    if (response.error) {
      throw new Error(`Failed to fetch task parcels: ${response.error}`);
    }
    return withoutHiddenGuesses(response.data as TaskParcel[]);
  },
  enabled: !!jobId && !!taskId,
  // Signed URLs expire after an hour; keep well inside that.
  staleTime: 5 * 60 * 1000,
});

// How long a kept URL is reused before a fresh read replaces it — well inside
// the signed URL's hour, so a frame already on screen re-signs before it expires.
export const KEEP_FRAME_URL_MS = 30 * 60 * 1000;

export interface KeptFrameUrl {
  url: string;
  keptAt: number;
}

/**
 * Each parcel's URL for this task: the first one seen, kept for
 * `KEEP_FRAME_URL_MS`. lebron re-signs every image on every /parcels read, and
 * the SAM 2 prefill polls it every few seconds — a fresh URL for the same frame
 * would re-download it and, since load status is keyed by URL, hide its boxes
 * until it had loaded again.
 */
export const keepFrameUrls = (
  kept: Record<string, KeptFrameUrl>,
  parcels: Pick<TaskParcel, "parcel_id" | "image" | "optimized_image">[],
  now: number = Date.now(),
): Record<string, KeptFrameUrl> => {
  const next = { ...kept };
  for (const p of parcels) {
    const prev = next[p.parcel_id];
    if (prev && now - prev.keptAt < KEEP_FRAME_URL_MS) continue;
    next[p.parcel_id] = { url: p.optimized_image ?? p.image, keptAt: now };
  }
  return next;
};

/**
 * A task's parcels as an ordered frame sequence for the scrubber, preloaded
 * outward from the primary (closest first), each carrying its load status.
 * Returns null when disabled or when the task has no context parcels, so the
 * UI stays a single frame.
 */
export const useTaskRelatedFrames = (
  jobId: string,
  taskId: string,
  enabled: boolean,
  includeSam2 = false,
): RelatedFrame[] | null => {
  const { data } = useQuery({
    ...taskParcelsQueryOptions(jobId, taskId, { includeSam2 }),
    enabled: enabled && !!jobId && !!taskId,
  });

  // Kept per task (see keepFrameUrls); a URL that fails to load is dropped so
  // the next read's fresh one replaces it without waiting out the keep window.
  const keptUrls = useRef<{
    taskId: string;
    urls: Record<string, KeptFrameUrl>;
  }>({
    taskId,
    urls: {},
  });
  if (keptUrls.current.taskId !== taskId)
    keptUrls.current = { taskId, urls: {} };
  const resolved = useMemo(() => {
    if (!data) return undefined;
    const urls = keepFrameUrls(keptUrls.current.urls, data);
    keptUrls.current.urls = urls;
    return data.map((p) => ({ ...p, url: urls[p.parcel_id].url }));
  }, [data, taskId]);

  // Preload restarts only when the frames' URLs change, not on every poll.
  const primaryIdx = Math.max(
    0,
    resolved?.findIndex((f) => f.role === "PRIMARY") ?? 0,
  );
  const urlsKey = resolved?.map((f) => f.url).join("\n") ?? "";

  const [statusByUrl, setStatusByUrl] = useState<
    Record<string, FrameLoadStatus>
  >({});

  useEffect(() => {
    const urls = urlsKey ? urlsKey.split("\n") : [];
    if (urls.length < 2) return;
    const queue = urls
      .map((url, idx) => ({ url, distance: Math.abs(idx - primaryIdx) }))
      .sort((a, b) => a.distance - b.distance)
      .map((f) => f.url);

    let cancelled = false;
    const inFlight: HTMLImageElement[] = [];
    const next = () => {
      const url = queue.shift();
      if (!url || cancelled) return;
      const img = new Image();
      inFlight.push(img);
      const settle = (status: FrameLoadStatus) => {
        if (cancelled) return;
        if (status === "error") {
          const kept = keptUrls.current.urls;
          for (const id of Object.keys(kept))
            if (kept[id].url === url) delete kept[id];
        }
        setStatusByUrl((prev) => ({ ...prev, [url]: status }));
        next();
      };
      img.onload = () => settle("loaded");
      img.onerror = () => settle("error");
      img.src = url;
    };
    for (let i = 0; i < PRELOAD_CONCURRENCY; i++) next();

    return () => {
      cancelled = true;
      inFlight.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [urlsKey, primaryIdx]);

  const frames = useMemo(
    () =>
      resolved?.map((f) => ({
        ...f,
        status: statusByUrl[f.url] ?? ("loading" as const),
      })) ?? null,
    [resolved, statusByUrl],
  );

  return enabled && frames && frames.length > 1 ? frames : null;
};
